import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';

import { UseOutletContextRecurringContract } from './RecurringContract';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { RecurringContractDA } from '../../../../services/admin/recurring.contract.service';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { formatContact, formatCurrency, formatMoney, formatNumberAddress, normalizeContactData } from '../../../../scripts/format.value.script';
import { getError, getMessage } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { countryName } from '../../../../libraries/country.library';
import { formatCantonDisplay } from '../../../../data/cantons';
import { languageValue } from '../../../../libraries/language.library';
import { qrTypeValue } from '../../../../libraries/qr.type.library';
import { contactTypeValue } from '../../../../libraries/contact.type.library';

import AppSaleViewRequirements from '../component/SaleViewRequirements';
import AppSaleViewAmount from '../component/SaleViewAmount';
import AppPreloader from '../../../../components/Preloader';
import AppModalDepositReceive from '../../../../components/modal/ModalDepositReceive';
import AppModalDepositReturn from '../../../../components/modal/ModalDepositReturn';
import { modalShow } from '../../../../utils/modal.util';

export interface RecurringContractViewPageProps {};

let errorResponse: Error;

const RecurringContractViewPage: React.FunctionComponent<RecurringContractViewPageProps> = props => {
  const {setRoute, companyForUser, settingsForCompany, treasuryAccountsForCompany} = UseOutletContextRecurringContract()
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [contract, setContract] = useState<any | null>(null)

  const loadContract = async (id_company: string, id_contract: string) => {
    try {
      const response = await RecurringContractDA.getRecurringContract(id_company, id_contract)
      if (response.status === 200) {
        const data = response.data
        if (data.contact) normalizeContactData(data.contact)
        setContract(data)
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse?.code, lang.code, errorResponse?.message),
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        }).then(() => {
          navigate('/main/sale/recurring-contract/list', {replace: true})
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  const statusColor = (status: string): string => {
    switch (status) {
      case 'active': return 'success'
      case 'paused': return 'warning'
      case 'ended': return 'secondary'
      case 'error': return 'danger'
      default: return 'secondary'
    }
  }

  const statusLabel = (status: string): string => {
    switch (status) {
      case 'active': return lang.labels.active
      case 'paused': return lang.labels.paused || 'Paused'
      case 'ended': return lang.labels.ended || 'Ended'
      case 'error': return lang.labels.error || 'Error'
      default: return status
    }
  }

  const cadenceLabel = (cadence: any): string => {
    if (!cadence) return '-'
    const { unit, interval } = cadence
    if (interval === 1) {
      switch (unit) {
        case 'month': return lang.labels.monthly || 'Monthly'
        case 'quarter': return lang.labels.quarterly || 'Quarterly'
        case 'year': return lang.labels.annual || 'Annual'
        default: return unit
      }
    }
    const unitLabel = unit === 'month' ? (lang.labels.months || 'months')
      : unit === 'quarter' ? (lang.labels.quarters || 'quarters')
      : (lang.labels.years || 'years')
    return `${lang.labels.every || 'Every'} ${interval} ${unitLabel}`
  }

  const depositStatusColor = (status: string): string => {
    switch (status) {
      case 'none': return 'secondary'
      case 'pending': return 'warning'
      case 'received': return 'success'
      case 'partially_retained': return 'info'
      case 'returned': return 'primary'
      default: return 'secondary'
    }
  }

  const depositStatusLabel = (status: string): string => {
    switch (status) {
      case 'none': return lang.labels.depositNone
      case 'pending': return lang.labels.depositPending
      case 'received': return lang.labels.depositReceived
      case 'partially_retained': return lang.labels.depositPartiallyRetained
      case 'returned': return lang.labels.depositReturned
      default: return status
    }
  }

  const submitRenew = async () => {
    if (!companyForUser || !contract) return

    const result = await GlobalSwal.fire({
      title: lang.labels.renewNow || 'Renew now',
      text: lang.labels.renewConfirm || 'The contract will be extended by one additional period.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesConfirm || 'Yes',
      cancelButtonText: lang.labels.noCancel
    })

    if (!result.isConfirmed) return

    try {
      const response = await RecurringContractDA.renewRecurringContract(companyForUser.id, contract.id)
      if (response.status === 200) {
        setContract(response.data)
        GlobalSwal.fire({
          title: lang.labels.contractRenewed || 'Contract renewed',
          icon: 'success',
          showConfirmButton: false,
          timer: 1500
        })
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse?.code, lang.code, errorResponse?.message),
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  const submitStatusChange = async (newStatus: string) => {
    if (!companyForUser || !contract) return

    const result = await GlobalSwal.fire({
      title: `${lang.labels.changeStatusTo || 'Change status to'} ${statusLabel(newStatus).toLowerCase()}?`,
      text: lang.labels.youWillNotBeAbleToUndo,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesConfirm || 'Yes',
      cancelButtonText: lang.labels.noCancel
    })

    if (!result.isConfirmed) return

    try {
      const response = await RecurringContractDA.putRecurringContractStatus(companyForUser.id, contract.id, newStatus)
      if (response.status === 200) {
        setContract({...contract, status: newStatus})
        GlobalSwal.fire({
          title: lang.labels.successfullyUpdatedStatus,
          icon: 'success',
          showConfirmButton: false,
          timer: 1500
        })
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse?.code, lang.code, errorResponse?.message),
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  const submitGenerateInvoice = async () => {
    if (!companyForUser || !contract) return

    const result = await GlobalSwal.fire({
      title: lang.labels.generateInvoice || 'Generate invoice now?',
      text: lang.labels.generateInvoiceConfirm || 'An invoice will be created from this contract.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesConfirm || 'Yes',
      cancelButtonText: lang.labels.noCancel
    })

    if (!result.isConfirmed) return

    try {
      const response = await RecurringContractDA.generateInvoice(companyForUser.id, contract.id)
      if (response.status === 200 || response.status === 201) {
        GlobalSwal.fire({
          title: lang.labels.invoiceGenerated || 'Invoice generated',
          icon: 'success',
          showConfirmButton: false,
          timer: 2200
        })
        loadContract(companyForUser.id, contract.id)
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse?.code, lang.code, errorResponse?.message),
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  const submitDelete = async () => {
    if (!companyForUser || !contract) return

    const result = await GlobalSwal.fire({
      title: lang.labels.areYouSure,
      text: lang.labels.youWillNotBeAbleToUndo,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesDelete,
      cancelButtonText: lang.labels.noCancel
    })

    if (!result.isConfirmed) return

    try {
      const response = await RecurringContractDA.deleteRecurringContract(companyForUser.id, contract.id)
      if (response.status === 200) {
        GlobalSwal.fire({
          title: lang.labels.successfullyDeleted,
          icon: 'success',
          showConfirmButton: false,
          timer: 1500
        }).then(() => {
          navigate('/main/sale/recurring-contract/list', {replace: true})
        })
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse?.code, lang.code, errorResponse?.message),
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  useEffect( () => {
    if (companyForUser && param.id) {
      loadContract(companyForUser.id, param.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.recurringContracts || 'Recurring contracts', branch: lang.labels.viewRecurringContract || 'View recurring contract'}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && contract
        ?
        <>
        <div className="d-flex flex-column flex-lg-row">
          <div className="flex-lg-row-fluid order-2 order-lg-1 mb-5 mb-lg-0 me-lg-5">
            <div className="card card-flush mb-5">
              <div className="card-header pt-3">
                <div className="card-title align-items-start flex-column">
                  <div className="d-flex align-items-center gap-3">
                    <h1 className="mb-0">{contract.contract_number || contract.label || contract.template?.concerning || '-'}</h1>
                  </div>
                  <label className="fs-6 fw-bold mt-1">
                    {lang.labels.status}:
                    <span className={`badge badge-light-${statusColor(contract.status)} text-uppercase fs-7 ms-2`}>{statusLabel(contract.status)}</span>
                  </label>
                </div>
                <div className="card-toolbar">
                  {contract.status !== 'ended' && contract.end_date && (
                    <button className="btn btn-light-primary mb-1 me-2" type="button" onClick={submitRenew}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-arrow-repeat me-2"></i>
                        {lang.labels.renewNow || 'Renew now'}
                      </div>
                    </button>
                  )}
                  {contract.status === 'active' && (
                    <>
                      <button className="btn btn-primary mb-1 me-2" type="button" onClick={submitGenerateInvoice}>
                        <div className="d-flex align-items-center">
                          <span className="svg-icon svg-icon-2 ms-0">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path opacity="0.3" d="M14 3V20H2V3C2 2.4 2.4 2 3 2H13C13.6 2 14 2.4 14 3ZM11 13V11C11 9.7 10.2 8.59995 9 8.19995V7C9 6.4 8.6 6 8 6C7.4 6 7 6.4 7 7V8.19995C5.8 8.59995 5 9.7 5 11V13C5 13.6 4.6 14 4 14V15C4 15.6 4.4 16 5 16H11C11.6 16 12 15.6 12 15V14C11.4 14 11 13.6 11 13Z" fill="black"/>
                              <path d="M2 20H14V21C14 21.6 13.6 22 13 22H3C2.4 22 2 21.6 2 21V20ZM9 3V2H7V3C7 3.6 7.4 4 8 4C8.6 4 9 3.6 9 3Z" fill="black"/>
                            </svg>
                          </span>
                          {lang.labels.generateInvoice || 'Generate invoice'}
                        </div>
                      </button>
                      <button className="btn btn-light-warning mb-1 me-2" type="button" onClick={() => submitStatusChange('paused')}>
                        <div className="d-flex align-items-center">
                          <span className="svg-icon svg-icon-2 ms-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M8 5V19M16 5V19" stroke="black" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </span>
                          {lang.labels.pause || 'Pause'}
                        </div>
                      </button>
                    </>
                  )}
                  {contract.status === 'paused' && (
                    <button className="btn btn-light-success mb-1 me-2" type="button" onClick={() => submitStatusChange('active')}>
                      <div className="d-flex align-items-center">
                        <span className="svg-icon svg-icon-2 ms-0">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M8 5.14V19.14L19 12.14L8 5.14Z" fill="black"/>
                          </svg>
                        </span>
                        {lang.labels.activate || 'Activate'}
                      </div>
                    </button>
                  )}
                  {contract.status !== 'ended' && (
                    <Link to={"/main/sale/recurring-contract/update/" + contract.id} className="btn btn-primary mb-1 me-2">
                      <div className="d-flex align-items-center">
                        <span className="svg-icon svg-icon-2 ms-0">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path opacity="0.3" d="M2 4.63158C2 3.1782 3.1782 2 4.63158 2H13.47C14.0155 2 14.278 2.66919 13.8778 3.04006L12.4556 4.35821C11.9009 4.87228 11.1726 5.15789 10.4163 5.15789H7.1579C6.05333 5.15789 5.15789 6.05333 5.15789 7.1579V16.8421C5.15789 17.9467 6.05333 18.8421 7.1579 18.8421H16.8421C17.9467 18.8421 18.8421 17.9467 18.8421 16.8421V13.7518C18.8421 12.927 19.1817 12.1387 19.7809 11.572L20.9878 10.4308C21.3703 10.0691 22 10.3403 22 10.8668V19.3684C22 20.8218 20.8218 22 19.3684 22H4.63158C3.1782 22 2 20.8218 2 19.3684V4.63158Z" fill="black"/>
                            <path d="M10.9256 11.1882C10.5351 10.7977 10.5351 10.1645 10.9256 9.77397L18.0669 2.6327C18.8479 1.85165 20.1143 1.85165 20.8953 2.6327L21.3665 3.10391C22.1476 3.88496 22.1476 5.15129 21.3665 5.93234L14.2252 13.0736C13.8347 13.4641 13.2016 13.4641 12.811 13.0736L10.9256 11.1882Z" fill="black"/>
                            <path d="M8.82343 12.0064L8.08852 14.3348C7.8655 15.0414 8.46151 15.7366 9.19388 15.6242L11.8974 15.2092C12.4642 15.1222 12.6916 14.4278 12.2861 14.0223L9.98595 11.7221C9.61452 11.3507 8.98154 11.5055 8.82343 12.0064Z" fill="black"/>
                          </svg>
                        </span>
                        {lang.labels.edit}
                      </div>
                    </Link>
                  )}
                  <Dropdown>
                    <Dropdown.Toggle variant="light" className="mb-1">
                      <i className="bi bi-three-dots fs-3"></i>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {contract.status === 'active' && (
                        <Dropdown.Item onClick={() => submitStatusChange('ended')}>
                          <i className="bi bi-x-circle me-2"></i>{lang.labels.ended || 'End'}
                        </Dropdown.Item>
                      )}
                      {contract.history?.length === 0 && (
                        <>
                          <Dropdown.Divider />
                          <Dropdown.Item className="text-danger" onClick={submitDelete}>
                            <i className="bi bi-trash me-2"></i>{lang.labels.delete}
                          </Dropdown.Item>
                        </>
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
              <div className="card-body">
                <div className="d-flex flex-wrap">
                  <div className="flex-equal me-5">
                    <table className="table align-middle gy-1 m-0">
                      <tbody className="fw-bold">
                        {contract.label && contract.contract_number && (
                          <tr>
                            <td className="w-200px text-muted">{lang.labels.title || 'Title'}:</td>
                            <td className="min-w-200px">{contract.label}</td>
                          </tr>
                        )}
                        {contract.template?.concerning && (
                          <tr>
                            <td className="w-200px text-muted">{lang.labels.description}:</td>
                            <td className="min-w-200px">{contract.template.concerning}</td>
                          </tr>
                        )}
                        <tr>
                          <td valign="top" className="w-200px text-muted">{lang.labels.customer}:</td>
                          <td className="min-w-200px">
                            <div className="d-flex align-items-center">
                              {formatContact(contract.contact)}
                              <span className="badge badge-sm badge-light text-uppercase fs-9 ms-3">{contactTypeValue(lang, contract.contact?.type ?? '')}</span>
                            </div>
                            {(contract.contact?.data?.contact_code ?? '').length > 0 &&
                              <p className="text-secondary fs-8 p-0 m-0">{lang.labels.code}: {contract.contact.data.contact_code}</p>
                            }
                            <p className="text-secondary fs-8 p-0 m-0">{contract.contact?.data?.address?.street ?? ''} {formatNumberAddress(lang, contract.contact?.data?.address?.number ?? '')}</p>
                            <p className="text-secondary fs-8 p-0 m-0">{contract.contact?.data?.address?.city ?? ''}{(contract.contact?.data?.address?.zip_code ?? '').length > 0 && ` - ${contract.contact.data.address.zip_code}`}</p>
                            {(contract.contact?.data?.address?.administrative_area ?? '').length > 0 &&
                              <p className="text-secondary fs-8 p-0 m-0">{formatCantonDisplay(contract.contact.data.address.administrative_area, lang.code)}</p>
                            }
                            <p className="text-secondary fs-8 p-0 m-0">{countryName(lang.code, contract.contact?.data?.address?.country ?? '')}</p>
                          </td>
                        </tr>
                        <tr>
                          <td className="w-200px text-muted">{lang.labels.currency}:</td>
                          <td className="min-w-200px">{formatCurrency(contract.template?.currency)}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-muted">{lang.labels.treasuryAccount}:</td>
                          <td className="min-w-200px">{contract.template?.treasury_account?.name ?? '-'}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-muted">{lang.labels.language}:</td>
                          <td className="min-w-200px">{languageValue(lang, contract.template?.language ?? '')}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-muted">{lang.labels.qrType}:</td>
                          <td className="min-w-200px">{qrTypeValue(lang, contract.template?.qr_type ?? '')}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="flex-equal">
                    <table className="table align-middle gy-1 m-0">
                      <tbody className="fw-bold">
                        <tr>
                          <td className="w-200px text-muted">{lang.labels.frequency || 'Frequency'}:</td>
                          <td className="min-w-200px">{cadenceLabel(contract.cadence)}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-muted">{lang.labels.anchorDay || 'Day of month'}:</td>
                          <td className="min-w-200px">{contract.cadence?.anchor_day ?? '-'}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-muted">{lang.labels.startDate || 'Start date'}:</td>
                          <td className="min-w-200px">{contract.start_date ? dateFormat(lang.code, contract.start_date) : '-'}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-muted">{lang.labels.endDate || 'End date'}:</td>
                          <td className="min-w-200px">{contract.end_date ? dateFormat(lang.code, contract.end_date) : '-'}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-muted">{lang.labels.nextInvoiceDate || 'Next invoice date'}:</td>
                          <td className="min-w-200px">{contract.next_issue_date ? dateFormat(lang.code, contract.next_issue_date) : '-'}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-muted">{lang.labels.paymentTermsDays || 'Payment terms (days)'}:</td>
                          <td className="min-w-200px">{contract.template?.payment_terms_days ?? 30}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-muted">{lang.labels.issueOffsetDays || 'Issue offset (days)'}:</td>
                          <td className="min-w-200px">{contract.issue_offset_days ?? 0}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-muted">{lang.labels.autoValidate || 'Auto validate'}:</td>
                          <td className="min-w-200px">{contract.auto_validate ? (lang.labels.yes || 'Yes') : (lang.labels.no || 'No')}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-muted">{lang.labels.autoSend || 'Auto send'}:</td>
                          <td className="min-w-200px">{contract.auto_send ? (lang.labels.yes || 'Yes') : (lang.labels.no || 'No')}</td>
                        </tr>
                        <tr>
                          <td className="w-200px text-muted">{lang.labels.autoRenew || 'Auto renew'}:</td>
                          <td className="min-w-200px">{contract.auto_renew ? (lang.labels.yes || 'Yes') : (lang.labels.no || 'No')}</td>
                        </tr>
                        { contract.auto_renew &&
                          <tr>
                            <td className="w-200px text-muted">{lang.labels.renewDaysBefore || 'Renew days before end'}:</td>
                            <td className="min-w-200px">{contract.renew_days_before ?? 0}</td>
                          </tr>
                        }
                        { (contract.renewal_count ?? 0) > 0 &&
                          <tr>
                            <td className="w-200px text-muted">{lang.labels.renewalCount || 'Renewals'}:</td>
                            <td className="min-w-200px">{contract.renewal_count}</td>
                          </tr>
                        }
                        {contract.indexation && (
                          <tr>
                            <td className="w-200px text-muted">{'Indexation'}:</td>
                            <td className="min-w-200px">{contract.indexation.rate}% — {contract.indexation.next_apply_date ? dateFormat(lang.code, contract.indexation.next_apply_date) : '-'}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            {contract.template?.requirements && contract.template.requirements.length > 0 && (
              <AppSaleViewRequirements record={contract.template}></AppSaleViewRequirements>
            )}
            {contract.template?.amount && (
              <AppSaleViewAmount sale={"recurring-contract"} record={contract.template}></AppSaleViewAmount>
            )}
            {contract.security_deposit && (
              <div className="card card-flush mb-5">
                <div className="card-header">
                  <div className="card-title">
                    <h4>{lang.labels.securityDeposit}</h4>
                  </div>
                  <div className="card-toolbar">
                    {(contract.security_deposit.status === 'none' || contract.security_deposit.status === 'pending') && contract.status === 'active' && (
                      <button className="btn btn-primary me-2" type="button" onClick={() => modalShow('modal-deposit-receive')}>
                        {lang.labels.receiveDeposit}
                      </button>
                    )}
                    {contract.security_deposit.status === 'received' && (
                      <button className="btn btn-primary" type="button" onClick={() => modalShow('modal-deposit-return')}>
                        {lang.labels.returnDeposit}
                      </button>
                    )}
                  </div>
                </div>
                <div className="card-body pt-0">
                  <div className="d-flex flex-wrap">
                    <div className="flex-equal me-5">
                      <table className="table align-middle gy-1 m-0">
                        <tbody className="fw-bold">
                          <tr>
                            <td className="w-200px text-muted">{lang.labels.depositStatus}:</td>
                            <td className="min-w-200px">
                              <span className={`badge badge-light-${depositStatusColor(contract.security_deposit.status)}`}>
                                {depositStatusLabel(contract.security_deposit.status)}
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td className="w-200px text-muted">{lang.labels.depositAmount}:</td>
                            <td className="min-w-200px" style={{fontVariantNumeric: 'tabular-nums'}}>
                              {contract.template?.currency?.code ?? 'CHF'} {formatMoney(contract.security_deposit.amount ?? 0)}
                            </td>
                          </tr>
                          {contract.security_deposit.received_at && (
                            <tr>
                              <td className="w-200px text-muted">{lang.labels.receivedAt}:</td>
                              <td className="min-w-200px">{dateFormat(lang.code, contract.security_deposit.received_at)}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {(contract.security_deposit.status === 'partially_retained' || contract.security_deposit.status === 'returned') && (
                      <div className="flex-equal">
                        <table className="table align-middle gy-1 m-0">
                          <tbody className="fw-bold">
                            {contract.security_deposit.returned_at && (
                              <tr>
                                <td className="w-200px text-muted">{lang.labels.returnedAt}:</td>
                                <td className="min-w-200px">{dateFormat(lang.code, contract.security_deposit.returned_at)}</td>
                              </tr>
                            )}
                            {(contract.security_deposit.retained_amount ?? 0) > 0 && (
                              <tr>
                                <td className="w-200px text-muted">{lang.labels.retainedAmount}:</td>
                                <td className="min-w-200px text-danger" style={{fontVariantNumeric: 'tabular-nums'}}>
                                  {contract.template?.currency?.code ?? 'CHF'} {formatMoney(contract.security_deposit.retained_amount)}
                                </td>
                              </tr>
                            )}
                            <tr>
                              <td className="w-200px text-muted">{lang.labels.returnedAmount}:</td>
                              <td className="min-w-200px" style={{fontVariantNumeric: 'tabular-nums'}}>
                                {contract.template?.currency?.code ?? 'CHF'} {formatMoney(contract.security_deposit.returned_amount ?? 0)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {(contract.lifecycle_history?.length > 0 || contract.renewal_count > 0) && (
              <div className="card card-flush mb-5">
                <div className="card-header">
                  <div className="card-title">
                    <h4>{lang.labels.lifecycleHistory || 'Lifecycle history'}</h4>
                  </div>
                </div>
                <div className="card-body pt-0">
                  { contract.lifecycle_history?.length > 0
                    ?
                    <div className="table-responsive">
                      <table className="table align-middle table-row-dashed mb-0">
                        <thead className="border-bottom">
                          <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                            <th>{lang.labels.type}</th>
                            <th>{lang.labels.date}</th>
                            <th>{lang.labels.origin || 'Origin'}</th>
                            <th>{lang.labels.endDate || 'End date'}</th>
                            <th>{lang.labels.note || 'Note'}</th>
                          </tr>
                        </thead>
                        <tbody className="text-secondary fw-bold">
                          {contract.lifecycle_history.map((event: any, index: number) => (
                            <tr key={index} className="row-dashed">
                              <td>
                                <span className={`badge badge-light-${event.type === 'renewed' ? 'success' : 'secondary'}`}>
                                  {event.type === 'renewed' ? (lang.labels.eventRenewed || 'Renewed') : (lang.labels.eventEnded || 'Ended')}
                                </span>
                              </td>
                              <td className="text-nowrap">{event.date ? dateFormat(lang.code, event.date) : '-'}</td>
                              <td>{event.trigger === 'auto' ? (lang.labels.triggerAuto || 'Automatic') : (lang.labels.triggerManual || 'Manual')}</td>
                              <td className="text-nowrap">
                                {event.previous_end_date ? dateFormat(lang.code, event.previous_end_date) : '-'}
                                {event.new_end_date && <> → {dateFormat(lang.code, event.new_end_date)}</>}
                              </td>
                              <td>{event.note || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    :
                    <p className="text-muted mb-0">{lang.labels.noLifecycleEvents || 'No lifecycle events'}</p>
                  }
                </div>
              </div>
            )}
            {contract.generated_invoices && contract.generated_invoices.length > 0 && (
              <div className="card card-flush mb-5">
                <div className="card-header">
                  <div className="card-title">
                    <h4>{lang.labels.generatedInvoices || 'Generated invoices'}</h4>
                  </div>
                </div>
                <div className="card-body pt-0">
                  <div className="table-responsive">
                    <table className="table align-middle table-row-dashed mb-0">
                      <thead className="border-bottom">
                        <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                          <th>{lang.labels.invoiceNumber || 'Invoice number'}</th>
                          <th>{lang.labels.date}</th>
                          <th className="text-end">{lang.labels.amountTotal}</th>
                        </tr>
                      </thead>
                      <tbody className="text-secondary fw-bold">
                        {contract.generated_invoices.map((inv: any, index: number) => (
                          <tr key={index} className="row-dashed">
                            <td>
                              <Link to={"/main/sale/invoice/view/" + inv.id} className="fw-bolder text-dark text-hover-primary">{inv.invoice_number ?? '-'}</Link>
                            </td>
                            <td className="text-nowrap">{inv.created_date ? dateFormat(lang.code, inv.created_date) : '-'}</td>
                            <td className="text-end text-nowrap">{contract.template?.currency?.code ?? 'CHF'} {formatMoney(inv.amount?.total ?? 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {contract.security_deposit && (
          <>
            <AppModalDepositReceive companyForUser={companyForUser} treasuryAccountsForCompany={treasuryAccountsForCompany} contract={contract} setContract={setContract} />
            <AppModalDepositReturn companyForUser={companyForUser} treasuryAccountsForCompany={treasuryAccountsForCompany} contract={contract} setContract={setContract} />
          </>
        )}
        </>
        :
        <AppPreloader/>
      }
    </div>
  );
}

export default RecurringContractViewPage;
